/**
 * StudentSignal Filter Doctrine v1.1 — Filter Engine
 * Logic-first implementation for scholarship/grant filtering
 * 
 * v1.1 CHANGES:
 * - Funding Type elevated to primary filter (top of stack)
 * - Renewable/One-time removed as filters (display-only metadata)
 * 
 * LAYER ARCHITECTURE:
 * - Layer 0: Funding Type (Primary Scope) - Scholarship | Grant | All
 * - Layer 1: Hard Constraints (Truth Filters) - narrow dataset
 * - Layer 2: Preferences (Soft Filters) - guide discovery, off by default
 * - Layer 3: Signals & Convenience - read-only behavioral helpers
 */

// ==================== CONSTANTS ====================

// Funding Type options (Layer 0 - Primary Filter)
export const FUNDING_TYPES = [
  { value: '', label: 'All Funding' },
  { value: 'scholarship', label: 'Scholarships' },
  { value: 'grant', label: 'Grants' },
];

// Award amount increments per doctrine
export const AMOUNT_INCREMENTS = {
  LOW: 500,      // $500 increments up to $5,000
  MID: 1000,     // $1,000 increments from $5,000–$25,000
  HIGH: 5000,    // $5,000 increments above $25,000
};

// Predefined amount stops for slider
export const AMOUNT_STOPS = [
  0, 500, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 4500, 5000,
  6000, 7000, 8000, 9000, 10000, 11000, 12000, 13000, 14000, 15000,
  16000, 17000, 18000, 19000, 20000, 21000, 22000, 23000, 24000, 25000,
  30000, 35000, 40000, 45000, 50000, 55000, 60000, 65000, 70000, 75000,
  80000, 85000, 90000, 95000, 100000, null // null = open-ended ($100k+)
];

// Deadline windows
export const DEADLINE_WINDOWS = [
  { value: '', label: 'Any deadline' },
  { value: '1week', label: 'Within 1 week', days: 7 },
  { value: '2weeks', label: 'Within 2 weeks', days: 14 },
  { value: '1month', label: 'Within 1 month', days: 30 },
  { value: '3months', label: 'Within 3 months', days: 90 },
  { value: '6months', label: 'Within 6 months', days: 180 },
];

// Education levels (Layer 1 - Hard Constraint)
export const EDUCATION_LEVELS = [
  { value: 'high_school', label: 'High School' },
  { value: 'undergraduate', label: 'Undergraduate' },
  { value: 'graduate', label: 'Graduate' },
];

// Layer 2 - Ethnicity / Status Filter (Simplified)
// Single multi-select control with OR logic
export const ETHNICITY_STATUS_OPTIONS = [
  { value: 'hispanic_latino', label: 'Hispanic / Latino' },
  { value: 'black_african_american', label: 'Black / African American' },
  { value: 'asian', label: 'Asian' },
  { value: 'native_american', label: 'Native American / Alaska Native' },
  { value: 'pacific_islander', label: 'Pacific Islander' },
  { value: 'military', label: 'Military' },
  { value: 'lgbtq', label: 'LGBTQ+' },
];

// Legacy exports for backward compatibility (unused)
export const SPONSOR_CRITERIA = {};
export const PREFERENCE_FILTERS = {};

// ==================== DEFAULT FILTER STATE ====================

export const DEFAULT_FILTERS = {
  // Layer 0 - Primary Scope (Funding Type)
  fundingType: '', // '' = All, 'scholarship', 'grant'
  
  // Layer 1 - Hard Constraints
  amountMin: null,
  amountMax: null,
  // NOTE: includeUnknownAmounts removed - always true by default
  deadline: '',
  educationLevels: [], // Empty = all levels
  // NOTE: renewable removed as filter in v1.1 (display-only metadata now)
  
  // Layer 2 - Ethnicity / Status (simplified single filter)
  // Multi-select array with OR logic
  ethnicityStatus: [],
  
  // Search (not part of layer architecture, but needed)
  searchQuery: '',
};

// ==================== FILTER LOGIC ====================

/**
 * Parse amount string to numeric value
 * Handles formats like "$5,000", "Up to $10,000", "$1,000 - $5,000"
 */
export const parseAmount = (amountStr) => {
  if (!amountStr || amountStr === 'Varies' || amountStr === 'Unknown') {
    return { min: null, max: null, unknown: true };
  }
  
  const cleaned = amountStr.replace(/[$,]/g, '');
  
  // Handle "Up to X"
  if (cleaned.toLowerCase().includes('up to')) {
    const match = cleaned.match(/(\d+)/);
    return { min: null, max: match ? parseInt(match[1]) : null, unknown: false };
  }
  
  // Handle range "X - Y"
  const rangeMatch = cleaned.match(/(\d+)\s*[-–]\s*(\d+)/);
  if (rangeMatch) {
    return { 
      min: parseInt(rangeMatch[1]), 
      max: parseInt(rangeMatch[2]), 
      unknown: false 
    };
  }
  
  // Handle single value
  const singleMatch = cleaned.match(/(\d+)/);
  if (singleMatch) {
    const value = parseInt(singleMatch[1]);
    return { min: value, max: value, unknown: false };
  }
  
  return { min: null, max: null, unknown: true };
};

/**
 * Format amount for display
 */
export const formatAmountDisplay = (amount) => {
  if (amount === null || amount === undefined) return 'Any';
  if (amount >= 100000) return '$100k+';
  if (amount >= 1000) return `$${(amount / 1000).toFixed(amount % 1000 === 0 ? 0 : 1)}k`;
  return `$${amount}`;
};

/**
 * Get the nearest snapped amount value
 */
export const snapToIncrement = (value) => {
  if (value === null || value === undefined) return null;
  
  // Find closest stop
  let closest = AMOUNT_STOPS[0];
  let minDiff = Math.abs(value - (closest || 0));
  
  for (const stop of AMOUNT_STOPS) {
    if (stop === null) continue;
    const diff = Math.abs(value - stop);
    if (diff < minDiff) {
      minDiff = diff;
      closest = stop;
    }
  }
  
  return closest;
};

/**
 * Check if scholarship passes deadline filter
 */
export const passesDeadlineFilter = (scholarship, deadlineWindow) => {
  if (!deadlineWindow) return true;
  
  const windowConfig = DEADLINE_WINDOWS.find(w => w.value === deadlineWindow);
  if (!windowConfig || !windowConfig.days) return true;
  
  // If no deadline data, include by default (per doctrine)
  if (!scholarship.deadline && !scholarship.deadlineRaw) return true;
  
  const deadlineDate = scholarship.deadline ? new Date(scholarship.deadline) : null;
  if (!deadlineDate || isNaN(deadlineDate.getTime())) return true;
  
  const now = new Date();
  const cutoff = new Date(now.getTime() + (windowConfig.days * 24 * 60 * 60 * 1000));
  
  return deadlineDate <= cutoff && deadlineDate >= now;
};

/**
 * Check if scholarship passes amount filter
 * NOTE: Unknown amounts are always included by default (no toggle needed)
 */
export const passesAmountFilter = (scholarship, minAmount, maxAmount) => {
  const parsed = parseAmount(scholarship.amount);
  
  // Handle unknown amounts - always include per doctrine
  if (parsed.unknown) {
    return true;
  }
  
  // Use amountMax from scholarship if available (numeric field)
  const scholarshipMax = scholarship.amountMax || parsed.max;
  const scholarshipMin = scholarship.amountMin || parsed.min;
  
  // If no filter set, include all
  if (minAmount === null && maxAmount === null) return true;
  
  // Check min filter
  if (minAmount !== null && scholarshipMax !== null && scholarshipMax < minAmount) {
    return false;
  }
  
  // Check max filter (open-ended if maxAmount is null)
  if (maxAmount !== null && scholarshipMin !== null && scholarshipMin > maxAmount) {
    return false;
  }
  
  return true;
};

/**
 * Check if scholarship passes education level filter
 */
export const passesEducationLevelFilter = (scholarship, selectedLevels) => {
  // Empty selection = all levels (per doctrine: no default exclusions)
  if (!selectedLevels || selectedLevels.length === 0) return true;
  
  const scholarshipLevels = scholarship.levelOfStudy || [];
  
  // If scholarship has no level data, include by default (per doctrine)
  if (scholarshipLevels.length === 0) return true;
  
  // Check if any selected level matches
  return selectedLevels.some(level => scholarshipLevels.includes(level));
};

/**
 * Check if scholarship passes funding type filter (Layer 0 - Primary)
 */
export const passesFundingTypeFilter = (scholarship, fundingType) => {
  // Empty = All Funding (no filter)
  if (!fundingType || fundingType === '') return true;
  
  // Match against fundingType field
  const itemType = scholarship.fundingType?.toLowerCase() || '';
  return itemType === fundingType.toLowerCase();
};

// NOTE: passesRenewableFilter removed in v1.1 - renewable is now display-only metadata

/**
 * Check if scholarship passes ethnicity/status filter
 * OR logic: pass if ANY selected value matches
 * Returns true if no values selected (OFF by default)
 */
export const passesEthnicityStatusFilter = (scholarship, selectedValues) => {
  // If no values selected, pass all (OFF by default)
  if (!selectedValues || selectedValues.length === 0) return true;
  
  // Check in description/eligibility text for keyword matching
  const eligibilityText = [
    scholarship.eligibility,
    scholarship.description,
    scholarship.requirements,
    scholarship.category,
  ].filter(Boolean).join(' ').toLowerCase();
  
  // OR logic: pass if ANY selected value matches
  return selectedValues.some(value => {
    const config = ETHNICITY_STATUS_OPTIONS.find(o => o.value === value);
    if (config && eligibilityText.includes(config.label.toLowerCase())) {
      return true;
    }
    // Also check for partial matches
    if (value === 'military' && (eligibilityText.includes('military') || eligibilityText.includes('veteran'))) return true;
    if (value === 'lgbtq' && (eligibilityText.includes('lgbtq') || eligibilityText.includes('lgbt') || eligibilityText.includes('gay') || eligibilityText.includes('lesbian') || eligibilityText.includes('queer'))) return true;
    if (value === 'hispanic_latino' && (eligibilityText.includes('hispanic') || eligibilityText.includes('latino'))) return true;
    if (value === 'black_african_american' && (eligibilityText.includes('african american') || eligibilityText.includes('black'))) return true;
    if (value === 'asian' && eligibilityText.includes('asian')) return true;
    if (value === 'native_american' && (eligibilityText.includes('native american') || eligibilityText.includes('alaska native'))) return true;
    if (value === 'pacific_islander' && eligibilityText.includes('pacific islander')) return true;
    return false;
  });
};

/**
 * Check if scholarship passes search query
 */
export const passesSearchFilter = (scholarship, query) => {
  if (!query || query.trim() === '') return true;
  
  const searchTerms = query.toLowerCase().trim();
  const searchableText = [
    scholarship.name,
    scholarship.description,
    scholarship.sponsor,
    scholarship.category,
  ].filter(Boolean).join(' ').toLowerCase();
  
  return searchableText.includes(searchTerms);
};

// ==================== MAIN FILTER FUNCTION ====================

/**
 * Apply all filters to scholarships array
 * Returns filtered array plus metadata about filtering
 */
export const applyFilters = (scholarships, filters) => {
  if (!scholarships || !Array.isArray(scholarships)) {
    return { results: [], metadata: { total: 0, filtered: 0, zeroResultCause: null } };
  }
  
  const total = scholarships.length;
  let results = [...scholarships];
  const filterCounts = {};
  
  // Track which filters are active for zero-result detection
  const activeFilters = [];
  
  // Layer 0 - Primary Scope (Funding Type) - MUST BE FIRST
  if (filters.fundingType && filters.fundingType !== '') {
    activeFilters.push('fundingType');
    const beforeCount = results.length;
    results = results.filter(s => passesFundingTypeFilter(s, filters.fundingType));
    filterCounts.fundingType = beforeCount - results.length;
  }
  
  // Layer 1 - Hard Constraints
  
  // Amount filter (unknown amounts always included)
  if (filters.amountMin !== null || filters.amountMax !== null) {
    activeFilters.push('amount');
    const beforeCount = results.length;
    results = results.filter(s => 
      passesAmountFilter(s, filters.amountMin, filters.amountMax)
    );
    filterCounts.amount = beforeCount - results.length;
  }
  
  // Deadline filter
  if (filters.deadline) {
    activeFilters.push('deadline');
    const beforeCount = results.length;
    results = results.filter(s => passesDeadlineFilter(s, filters.deadline));
    filterCounts.deadline = beforeCount - results.length;
  }
  
  // Education level filter
  if (filters.educationLevels && filters.educationLevels.length > 0) {
    activeFilters.push('educationLevel');
    const beforeCount = results.length;
    results = results.filter(s => passesEducationLevelFilter(s, filters.educationLevels));
    filterCounts.educationLevel = beforeCount - results.length;
  }
  
  // Search filter
  if (filters.searchQuery) {
    activeFilters.push('search');
    const beforeCount = results.length;
    results = results.filter(s => passesSearchFilter(s, filters.searchQuery));
    filterCounts.search = beforeCount - results.length;
  }
  
  // Layer 2 - Ethnicity / Status (simplified single filter)
  if (filters.ethnicityStatus && filters.ethnicityStatus.length > 0) {
    activeFilters.push('ethnicityStatus');
    const beforeCount = results.length;
    results = results.filter(s => passesEthnicityStatusFilter(s, filters.ethnicityStatus));
    filterCounts.ethnicityStatus = beforeCount - results.length;
  }
  
  // Determine zero-result cause if applicable
  let zeroResultCause = null;
  if (results.length === 0 && total > 0) {
    // Find the filter that caused the most exclusions
    const sortedFilters = Object.entries(filterCounts)
      .sort(([, a], [, b]) => b - a);
    
    if (sortedFilters.length > 0) {
      zeroResultCause = {
        filter: sortedFilters[0][0],
        excluded: sortedFilters[0][1],
        activeFilters: activeFilters,
      };
    }
  }
  
  return {
    results,
    metadata: {
      total,
      filtered: results.length,
      excluded: total - results.length,
      zeroResultCause,
      activeFilters,
      filterCounts,
    },
  };
};

// ==================== ACTIVE FILTER CHIPS ====================

/**
 * Generate list of active filter chips for display
 * Each chip has: key, label, onRemove callback info
 */
export const getActiveFilterChips = (filters) => {
  const chips = [];
  
  // Funding Type (Layer 0 - Primary)
  if (filters.fundingType && filters.fundingType !== '') {
    const typeConfig = FUNDING_TYPES.find(t => t.value === filters.fundingType);
    chips.push({
      key: 'fundingType',
      label: typeConfig?.label || filters.fundingType,
      removeKeys: ['fundingType']
    });
  }
  
  // Amount range
  if (filters.amountMin !== null || filters.amountMax !== null) {
    let label = 'Amount: ';
    if (filters.amountMin !== null && filters.amountMax !== null) {
      label += `${formatAmountDisplay(filters.amountMin)} – ${formatAmountDisplay(filters.amountMax)}`;
    } else if (filters.amountMin !== null) {
      label += `${formatAmountDisplay(filters.amountMin)}+`;
    } else {
      label += `Up to ${formatAmountDisplay(filters.amountMax)}`;
    }
    chips.push({ key: 'amount', label, removeKeys: ['amountMin', 'amountMax'] });
  }
  
  // NOTE: includeUnknownAmounts chip removed - always included by default
  
  // Deadline
  if (filters.deadline) {
    const windowConfig = DEADLINE_WINDOWS.find(w => w.value === filters.deadline);
    chips.push({ 
      key: 'deadline', 
      label: `Deadline: ${windowConfig?.label || filters.deadline}`,
      removeKeys: ['deadline'] 
    });
  }
  
  // Education levels
  if (filters.educationLevels && filters.educationLevels.length > 0) {
    const labels = filters.educationLevels.map(level => {
      const config = EDUCATION_LEVELS.find(l => l.value === level);
      return config?.label || level;
    });
    chips.push({ 
      key: 'educationLevels', 
      label: `Level: ${labels.join(', ')}`,
      removeKeys: ['educationLevels'] 
    });
  }
  
  // NOTE: Renewable chip removed in v1.1
  
  // Search query
  if (filters.searchQuery) {
    chips.push({ 
      key: 'searchQuery', 
      label: `Search: "${filters.searchQuery}"`,
      removeKeys: ['searchQuery'] 
    });
  }
  
  // Layer 2 - Ethnicity / Status (individual chips per selection)
  if (filters.ethnicityStatus && filters.ethnicityStatus.length > 0) {
    filters.ethnicityStatus.forEach(value => {
      const optionConfig = ETHNICITY_STATUS_OPTIONS.find(o => o.value === value);
      chips.push({
        key: `ethnicity_${value}`,
        label: optionConfig?.label || value,
        ethnicityValue: value,
        isEthnicityStatus: true,
      });
    });
  }
  
  return chips;
};

// ==================== URL QUERY SYNC ====================

/**
 * Convert filters object to URL query params
 */
export const filtersToQueryParams = (filters) => {
  const params = new URLSearchParams();
  
  // Layer 0 - Funding Type (Primary)
  if (filters.fundingType && filters.fundingType !== '') params.set('type', filters.fundingType);
  
  // Layer 1 - Hard Constraints
  if (filters.amountMin !== null) params.set('amountMin', filters.amountMin);
  if (filters.amountMax !== null) params.set('amountMax', filters.amountMax);
  // NOTE: includeUnknownAmounts removed - always included
  if (filters.deadline) params.set('deadline', filters.deadline);
  if (filters.educationLevels?.length > 0) params.set('levels', filters.educationLevels.join(','));
  if (filters.searchQuery) params.set('q', filters.searchQuery);
  
  // Layer 2 - Ethnicity / Status
  if (filters.ethnicityStatus?.length > 0) {
    params.set('status', filters.ethnicityStatus.join(','));
  }
  
  return params;
};

/**
 * Parse URL query params to filters object
 */
export const queryParamsToFilters = (searchParams) => {
  const filters = { ...DEFAULT_FILTERS };
  
  // Layer 0 - Funding Type
  const fundingType = searchParams.get('type');
  
  // Layer 1 - Hard Constraints
  const amountMin = searchParams.get('amountMin');
  const amountMax = searchParams.get('amountMax');
  // NOTE: knownOnly removed - always included
  const deadline = searchParams.get('deadline');
  const levels = searchParams.get('levels');
  const query = searchParams.get('q');
  
  if (fundingType) filters.fundingType = fundingType;
  if (amountMin) filters.amountMin = parseInt(amountMin);
  if (amountMax) filters.amountMax = parseInt(amountMax);
  if (deadline) filters.deadline = deadline;
  if (levels) filters.educationLevels = levels.split(',');
  if (query) filters.searchQuery = query;
  
  // Layer 2 - Ethnicity / Status
  const statusParam = searchParams.get('status');
  if (statusParam) {
    filters.ethnicityStatus = statusParam.split(',');
  }
  
  return filters;
};

/**
 * Check if any filters are active
 */
export const hasActiveFilters = (filters) => {
  return (
    (filters.fundingType && filters.fundingType !== '') ||
    filters.amountMin !== null ||
    filters.amountMax !== null ||
    filters.deadline !== '' ||
    (filters.educationLevels && filters.educationLevels.length > 0) ||
    filters.searchQuery !== '' ||
    (filters.ethnicityStatus && filters.ethnicityStatus.length > 0)
  );
};
